import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Truck, FileText, CheckCircle, MapPin, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, TextArea, Select } from '@/components/ui/Input';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { profileService } from '@/services/profileService';
import { VEHICLE_CATEGORY_LABELS } from '@/types';
import type { VehicleCategory } from '@/types';
import toast from 'react-hot-toast';

const STEPS = [
  { key: 'basic_info', label: 'Info Básica', icon: User },
  { key: 'vehicle', label: 'Vehículo', icon: Truck },
  { key: 'location', label: 'Ubicación', icon: MapPin },
  { key: 'documents', label: 'Documentos', icon: FileText },
  { key: 'review', label: 'Revisión', icon: CheckCircle },
];

const ZONES = [
  { value: 'cdmx_norte', label: 'CDMX Norte' },
  { value: 'cdmx_sur', label: 'CDMX Sur' },
  { value: 'cdmx_poniente', label: 'CDMX Poniente' },
  { value: 'cdmx_oriente', label: 'CDMX Oriente' },
  { value: 'edomex', label: 'Estado de México' },
  { value: 'monterrey', label: 'Monterrey' },
  { value: 'guadalajara', label: 'Guadalajara' },
  { value: 'queretaro', label: 'Querétaro' },
  { value: 'puebla', label: 'Puebla' },
  { value: 'merida', label: 'Mérida' },
];

export function OnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    display_name: '',
    bio: '',
    years_experience: '',
    preferred_zones: [] as string[],
    preferred_shifts: [] as string[],
    speaks_english: false,
    has_helpers: false,
    helper_count: 0,
    driver_type: 'independiente' as 'independiente' | 'flotillero',
    vehicle_category: '' as VehicleCategory | '',
    vehicle_label: '',
    address: '',
    latitude: '',
    longitude: '',
  });

  const updateForm = (key: string, value: unknown) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const toggleArrayValue = (key: 'preferred_zones' | 'preferred_shifts', value: string) => {
    setForm(prev => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter(v => v !== value)
        : [...prev[key], value],
    }));
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      if (!form.display_name.trim()) {
        toast.error('Tu nombre es obligatorio');
        return;
      }
      try {
        setLoading(true);
        await profileService.update({
          display_name: form.display_name,
          bio: form.bio,
          years_experience: form.years_experience ? parseInt(form.years_experience) : null,
          preferred_zones: form.preferred_zones,
          preferred_shifts: form.preferred_shifts,
          speaks_english: form.speaks_english,
          has_helpers: form.has_helpers,
          helper_count: form.helper_count,
          driver_type: form.driver_type,
        });
        await profileService.updateOnboardingStep('vehicle');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Error al guardar');
        return;
      } finally {
        setLoading(false);
      }
    }

    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleFinish = async () => {
    try {
      setLoading(true);
      await profileService.updateOnboardingStep('active');
      toast.success('¡Bienvenido a PartRunner! 🎉');
      navigate('/');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al completar');
    } finally {
      setLoading(false);
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-brand-black text-brand-white px-4 pt-12 pb-6">
        <img src="/images/logo-full-color.png" alt="PartRunner" className="h-8 mb-6" />
        <h1 className="font-heading text-3xl tracking-wider mb-2">CONFIGURA TU PERFIL</h1>
        <p className="text-gray-400 text-sm">Paso {currentStep + 1} de {STEPS.length}</p>
        <ProgressBar value={progress} className="mt-4" size="sm" />
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-2 px-4 py-4 overflow-x-auto">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <div
              key={step.key}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                i === currentStep
                  ? 'bg-brand-yellow text-brand-black'
                  : i < currentStep
                    ? 'bg-brand-black text-brand-white'
                    : 'bg-gray-100 text-gray-400'
              }`}
            >
              <Icon size={14} />
              {step.label}
            </div>
          );
        })}
      </div>

      {/* Step content */}
      <div className="px-4 py-4 space-y-4 animate-fade-in">
        {currentStep === 0 && (
          <>
            <Input
              label="Nombre completo"
              value={form.display_name}
              onChange={(e) => updateForm('display_name', e.target.value)}
              placeholder="Juan Pérez"
            />
            <TextArea
              label="Sobre ti (opcional)"
              value={form.bio}
              onChange={(e) => updateForm('bio', e.target.value)}
              placeholder="Cuéntanos sobre tu experiencia..."
            />
            <Input
              label="Años de experiencia"
              type="number"
              value={form.years_experience}
              onChange={(e) => updateForm('years_experience', e.target.value)}
              placeholder="5"
            />
            <Select
              label="Tipo de conductor"
              value={form.driver_type}
              onChange={(e) => updateForm('driver_type', e.target.value)}
              options={[
                { value: 'independiente', label: 'Independiente' },
                { value: 'flotillero', label: 'Flotillero' },
              ]}
            />

            <div>
              <p className="text-sm font-medium text-brand-black mb-2">Zonas preferidas</p>
              <div className="flex flex-wrap gap-2">
                {ZONES.map(z => (
                  <button
                    key={z.value}
                    onClick={() => toggleArrayValue('preferred_zones', z.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      form.preferred_zones.includes(z.value)
                        ? 'bg-brand-yellow text-brand-black'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {z.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-brand-black mb-2">Turnos preferidos</p>
              <div className="flex flex-wrap gap-2">
                {['morning', 'afternoon', 'night', 'full_day'].map(s => (
                  <button
                    key={s}
                    onClick={() => toggleArrayValue('preferred_shifts', s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      form.preferred_shifts.includes(s)
                        ? 'bg-brand-yellow text-brand-black'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {{ morning: '🌅 Mañana', afternoon: '☀️ Tarde', night: '🌙 Noche', full_day: '📅 Día completo' }[s]}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.speaks_english}
                onChange={(e) => updateForm('speaks_english', e.target.checked)}
                className="w-4 h-4 accent-brand-yellow"
              />
              <span className="text-sm">Hablo inglés</span>
            </div>
          </>
        )}

        {currentStep === 1 && (
          <>
            <p className="text-sm text-gray-500">Registra tu vehículo principal. Podrás agregar más después.</p>
            <Select
              label="Categoría del vehículo"
              value={form.vehicle_category}
              onChange={(e) => updateForm('vehicle_category', e.target.value)}
              options={Object.entries(VEHICLE_CATEGORY_LABELS).map(([v, l]) => ({ value: v, label: l }))}
            />
            <Input
              label="Etiqueta / nombre"
              value={form.vehicle_label}
              onChange={(e) => updateForm('vehicle_label', e.target.value)}
              placeholder="Mi camioneta"
            />
          </>
        )}

        {currentStep === 2 && (
          <>
            <p className="text-sm text-gray-500">¿Dónde estaciona tu vehículo habitualmente?</p>
            <Input
              label="Dirección"
              value={form.address}
              onChange={(e) => updateForm('address', e.target.value)}
              placeholder="Calle, Colonia, Ciudad"
            />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Latitud" value={form.latitude} onChange={(e) => updateForm('latitude', e.target.value)} placeholder="19.4326" type="number" step="any" />
              <Input label="Longitud" value={form.longitude} onChange={(e) => updateForm('longitude', e.target.value)} placeholder="-99.1332" type="number" step="any" />
            </div>
            <p className="text-xs text-gray-400">Puedes actualizar esto después desde la sección de Vehículos.</p>
          </>
        )}

        {currentStep === 3 && (
          <>
            <p className="text-sm text-gray-500">
              Sube tus documentos para completar tu perfil. Puedes hacerlo ahora o después desde la sección de Documentos.
            </p>
            <div className="space-y-3">
              {['INE', 'Licencia de Conducir', 'Comprobante de Domicilio'].map(doc => (
                <div key={doc} className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl">
                  <FileText size={20} className="text-gray-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{doc}</p>
                    <p className="text-xs text-gray-400">Pendiente</p>
                  </div>
                  <Button variant="outline" size="sm">Subir</Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400">Puedes completar los documentos más tarde.</p>
          </>
        )}

        {currentStep === 4 && (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-brand-yellow rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-brand-black" />
            </div>
            <h2 className="font-heading text-2xl tracking-wider mb-2">¡LISTO!</h2>
            <p className="text-sm text-gray-500 mb-6">
              Tu perfil está configurado. Ahora puedes explorar oportunidades, declarar tu disponibilidad y empezar a ganar puntos.
            </p>
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-4 flex gap-3 safe-area-bottom">
        {currentStep > 0 && (
          <Button variant="outline" onClick={() => setCurrentStep(prev => prev - 1)} className="flex-shrink-0">
            <ArrowLeft size={18} />
          </Button>
        )}
        {currentStep < STEPS.length - 1 ? (
          <Button fullWidth onClick={handleNext} loading={loading}>
            Continuar <ArrowRight size={18} className="ml-1" />
          </Button>
        ) : (
          <Button fullWidth onClick={handleFinish} loading={loading}>
            Comenzar 🎉
          </Button>
        )}
      </div>
    </div>
  );
}
